import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Calendar,
  Dumbbell,
  Target,
  TrendingUp,
  Clock,
  ArrowRight,
  Flame,
  Award,
  Plus,
} from "lucide-react";

export default function Dashboard() {
  const { user, plan, progressLogs } = useAuth();

  // Calculate stats
  const thisWeekWorkouts = progressLogs.filter((log) => {
    const logDate = new Date(log.workoutDate);
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return logDate >= weekAgo;
  }).length;

  const currentStreak = calculateStreak(progressLogs);
  const totalWorkouts = progressLogs.length;

  // Get today's workout from plan
  const today = new Date().toLocaleDateString("en-US", { weekday: "long" });
  const todayWorkout = plan?.weeklySchedule.find((day) => day.day === today);

  return (
    <div className="min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold">
            Welcome back, {user?.name || user?.email?.split("@")[0]}!
          </h1>
          <p className="text-muted-foreground mt-1">
            Here's your fitness overview for today
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Flame className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{currentStreak}</p>
                <p className="text-xs text-muted-foreground">Day Streak</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{thisWeekWorkouts}</p>
                <p className="text-xs text-muted-foreground">This Week</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Award className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalWorkouts}</p>
                <p className="text-xs text-muted-foreground">Total Workouts</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {plan ? `v${plan.version}` : "-"}
                </p>
                <p className="text-xs text-muted-foreground">Plan Version</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Today's Workout */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Dumbbell className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg">Today's Workout</h2>
                    <p className="text-sm text-muted-foreground">{today}</p>
                  </div>
                </div>
                <Link to="/plan">
                  <Button variant="outline" size="sm">
                    View Full Plan
                  </Button>
                </Link>
              </div>

              {todayWorkout ? (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="secondary">{todayWorkout.focus}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {todayWorkout.exercises.length} exercises
                    </span>
                  </div>
                  <div className="space-y-2">
                    {todayWorkout.exercises.slice(0, 3).map((exercise, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-card/50 rounded-lg"
                      >
                        <span className="font-medium">{exercise.name}</span>
                        <span className="text-sm text-muted-foreground">
                          {exercise.sets} × {exercise.reps}
                        </span>
                      </div>
                    ))}
                    {todayWorkout.exercises.length > 3 && (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        +{todayWorkout.exercises.length - 3} more exercises
                      </p>
                    )}
                  </div>
                  <Link to="/progress" className="mt-4 block">
                    <Button className="w-full gap-2 bg-amber-500 text-black hover:bg-lime-500">
                      <Plus className="w-4 h-4" />
                      Log Workout
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    {plan ? "Rest day! Take time to recover." : "No active plan yet."}
                  </p>
                  {!plan && (
                    <Link to="/onboarding">
                      <Button className="bg-amber-500 text-black hover:bg-lime-500">
                        Create Your Plan
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </Card>

            {/* Weekly Progress */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-lg">Weekly Progress</h2>
                <span className="text-sm text-muted-foreground">
                  {thisWeekWorkouts}/{(plan?.overview.frequency.match(/\d+/)?.[0]) || "5"} workouts
                </span>
              </div>
              <Progress
                value={thisWeekWorkouts * 20}
                className="h-3"
              />
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>Mon</span>
                <span>Tue</span>
                <span>Wed</span>
                <span>Thu</span>
                <span>Fri</span>
                <span>Sat</span>
                <span>Sun</span>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Plan Overview */}
            {plan && (
              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Target className="w-4 h-4 text-amber-500" />
                  Current Plan
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Goal</span>
                    <span className="font-medium">{plan.overview.goal}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Frequency</span>
                    <span className="font-medium">{plan.overview.frequency}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Split</span>
                    <span className="font-medium">{plan.overview.split}</span>
                  </div>
                </div>
                <Link to="/plan" className="mt-4 block">
                  <Button variant="outline" size="sm" className="w-full gap-2">
                    View Details
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="p-6">
              <h3 className="font-semibold mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link to="/exercises">
                  <Button variant="outline" size="sm" className="w-full justify-start gap-2">
                    <Dumbbell className="w-4 h-4" />
                    Find Exercises
                  </Button>
                </Link>
                <Link to="/progress">
                  <Button variant="outline" size="sm" className="w-full justify-start gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Log Workout
                  </Button>
                </Link>
                <Link to="/nutrition">
                  <Button variant="outline" size="sm" className="w-full justify-start gap-2">
                    <Clock className="w-4 h-4" />
                    Nutrition
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function calculateStreak(logs: { workoutDate: string }[]): number {
  if (logs.length === 0) return 0;
  
  const sortedDates = logs
    .map((log) => new Date(log.workoutDate).toDateString())
    .filter((date, index, self) => self.indexOf(date) === index)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  
  if (sortedDates.length === 0) return 0;
  
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString();
  
  let streak = 0;
  let checkDate = new Date();
  
  for (const dateStr of sortedDates) {
    const checkDateStr = checkDate.toDateString();
    if (dateStr === checkDateStr || (streak === 0 && dateStr === yesterday)) {
      streak++;
      checkDate = new Date(checkDate.getTime() - 24 * 60 * 60 * 1000);
    } else if (streak === 0 && dateStr !== yesterday) {
      break;
    }
  }
  
  return streak;
}
